import { useEffect, useRef, useState } from 'react'
import MediumModal from '../Modal/MediumModal'
import './AddClient.css'

const NEW_CLIENT_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/newClient'
const UPDATE_CLIENT_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/updateClient'

const emptyClient = {
    id: null,
    logo: '',
    nume: '',
    cui: '',
    website: '',
    note: ''
}

const emptyContact = {
    id: null,
    nume: '',
    functie: '',
    email: '',
    telefon: ''
}

const emptyAddress = {
    id: null,
    nume: '',
    adresa: ''
}

function AddClient({
    isOpen,
    mode = 'new',
    initialClient = null,
    onClose,
    onSaved
}) {
    const logoInputRef = useRef(null)
    const [saving, setSaving] = useState(false)

    const [clientForm, setClientForm] = useState(emptyClient)
    const [contactsForm, setContactsForm] = useState([emptyContact])
    const [addressesForm, setAddressesForm] = useState([emptyAddress])

    useEffect(() => {
        if (!isOpen) return

        const baseClient = {
            ...emptyClient,
            ...(initialClient || {})
        }

        setClientForm(baseClient)

        setContactsForm(
            Array.isArray(baseClient.contacte) && baseClient.contacte.length > 0
                ? baseClient.contacte
                : [{ ...emptyContact }]
        )

        setAddressesForm(
            Array.isArray(baseClient.adrese) && baseClient.adrese.length > 0
                ? baseClient.adrese
                : [{ ...emptyAddress }]
        )
    }, [isOpen, initialClient])

    function updateClientForm(field, value) {
        setClientForm((prev) => ({
            ...prev,
            [field]: value
        }))
    }

    function updateContactForm(index, field, value) {
        setContactsForm((prev) =>
            prev.map((contact, contactIndex) =>
                contactIndex === index
                    ? { ...contact, [field]: value }
                    : contact
            )
        )
    }

    function addContactForm() {
        setContactsForm((prev) => [...prev, { ...emptyContact }])
    }

    function removeContactForm(index) {
        setContactsForm((prev) => {
            if (prev.length === 1) return prev
            return prev.filter((_, contactIndex) => contactIndex !== index)
        })
    }

    function updateAddressForm(index, field, value) {
        setAddressesForm((prev) =>
            prev.map((address, addressIndex) =>
                addressIndex === index
                    ? { ...address, [field]: value }
                    : address
            )
        )
    }

    function addAddressForm() {
        setAddressesForm((prev) => [...prev, { ...emptyAddress }])
    }

    function removeAddressForm(index) {
        setAddressesForm((prev) => {
            if (prev.length === 1) return prev
            return prev.filter((_, addressIndex) => addressIndex !== index)
        })
    }

    async function saveClient() {
        try {
            setSaving(true)

            const payload = {
                id: clientForm.id,
                logo: clientForm.logo,
                nume: clientForm.nume,
                cui: clientForm.cui,
                website: clientForm.website,
                note: clientForm.note,
                contacte: contactsForm
                    .filter((contact) =>
                        contact.nume.trim() ||
                        contact.functie.trim() ||
                        contact.email.trim() ||
                        contact.telefon.trim()
                    )
                    .map((contact) => ({
                        id: contact.id || null,
                        nume: contact.nume || '',
                        functie: contact.functie || '',
                        email: contact.email || '',
                        telefon: contact.telefon || ''
                    })),
                adrese: addressesForm
                    .filter((address) =>
                        address.nume.trim() ||
                        address.adresa.trim()
                    )
                    .map((address) => ({
                        id: address.id || null,
                        nume: address.nume || '',
                        adresa: address.adresa || ''
                    }))
            }

            const response = await fetch(mode === 'new' ? NEW_CLIENT_API_URL : UPDATE_CLIENT_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })

            const data = await response.json()

            if (!response.ok || data.success === false) {
                throw new Error(data.message || 'Save client failed')
            }

            if (onSaved) {
                await onSaved(data.client || payload)
            }
        } catch (err) {
            console.error('SAVE CLIENT ERROR:', err)
            alert(err.message || 'Nu am putut salva clientul.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <MediumModal
            isOpen={isOpen}
            title={mode === 'new' ? 'Adaugă client' : 'Editează client'}
            onClose={onClose}
            footer={
                <>
                    <button
                        type="button"
                        className="modalSecondaryButton"
                        onClick={onClose}
                    >
                        Anulează
                    </button>

                    <button
                        type="button"
                        className="modalPrimaryButton"
                        disabled={saving}
                        onClick={saveClient}
                    >
                        {saving ? 'Se salvează...' : 'Salvează client'}
                    </button>
                </>
            }
        >
            <div className="clientPremiumModal">
                <div className="clientPremiumCard clientPremiumCompanyCard">
                    <aside className="clientPremiumLogoPanel">
                        <span className="clientPremiumLabel">Logo</span>

                        <button
                            type="button"
                            className="clientPremiumLogoDrop"
                            onClick={() => logoInputRef.current?.click()}
                        >
                            {clientForm.logo ? (
                                <img src={clientForm.logo} alt="Preview logo" />
                            ) : (
                                <svg viewBox="0 0 24 24">
                                    <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2ZM8.5 11.5l2.5 3.01L14.5 10l4.5 6H5l3.5-4.5Z" />
                                </svg>
                            )}
                        </button>

                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(event) => {
                                const file = event.target.files?.[0]

                                if (!file) return

                                if (!file.type.startsWith('image/')) {
                                    alert('Te rog încarcă o imagine.')
                                    event.target.value = ''
                                    return
                                }

                                const reader = new FileReader()

                                reader.onload = () => {
                                    updateClientForm('logo', reader.result)
                                }

                                reader.readAsDataURL(file)
                            }}
                        />

                        <button
                            type="button"
                            className="clientPremiumGhostButton"
                            onClick={() => logoInputRef.current?.click()}
                        >
                            Upload Logo
                        </button>

                        <div className="clientPremiumIconInput">
                            <svg viewBox="0 0 24 24">
                                <path d="M3.9 12a5 5 0 0 1 5-5h4v2h-4a3 3 0 0 0 0 6h4v2h-4a5 5 0 0 1-5-5Zm6.1 1v-2h4v2h-4Zm1-6h4a5 5 0 0 1 0 10h-4v-2h4a3 3 0 0 0 0-6h-4V7Z" />
                            </svg>

                            <input
                                type="text"
                                placeholder="URL imagine Logo"
                                value={clientForm.logo}
                                onChange={(event) => updateClientForm('logo', event.target.value)}
                            />
                        </div>
                    </aside>

                    <section className="clientPremiumMainPanel">
                                               <div className="clientPremiumInfoGrid">
                            <label className="clientPremiumField clientPremiumFullRow">
                                <span>Nume client *</span>
                                <input
                                    type="text"
                                    value={clientForm.nume}
                                    onChange={(event) => updateClientForm('nume', event.target.value)}
                                />
                            </label>

                            <label className="clientPremiumField">
                                <span>CUI</span>
                                <input
                                    type="text"
                                    value={clientForm.cui}
                                    onChange={(event) => updateClientForm('cui', event.target.value)}
                                />
                            </label>

                            <label className="clientPremiumField">
                                <span>Website</span>
                                <div className="clientPremiumIconInput">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.93 9h-3.02a15.7 15.7 0 0 0-1.18-5.03A8.02 8.02 0 0 1 18.93 11ZM12 4.04c.83 1.2 1.48 3.01 1.83 4.96h-3.66C10.52 7.05 11.17 5.24 12 4.04ZM4.26 13h3.83c.14 1.77.55 3.46 1.18 5.03A8.02 8.02 0 0 1 4.26 13Zm3.83-2H4.26a8.02 8.02 0 0 1 5.01-5.03A15.7 15.7 0 0 0 8.09 11ZM12 19.96c-.83-1.2-1.48-3.01-1.83-4.96h3.66c-.35 1.95-1 3.76-1.83 4.96Zm2.73-1.93A15.7 15.7 0 0 0 15.91 13h3.02a8.02 8.02 0 0 1-4.2 5.03Z" />
                                    </svg>

                                    <input
                                        type="text"
                                        value={clientForm.website}
                                        onChange={(event) => updateClientForm('website', event.target.value)}
                                    />
                                </div>
                            </label>
                        </div>

                        <div className="clientPremiumBottomGrid">
                            <label className="clientPremiumField clientPremiumNotes">
                                <span>Note</span>
                                <textarea
                                    placeholder="Adaugă o notă despre client..."
                                    value={clientForm.note}
                                    onChange={(event) => updateClientForm('note', event.target.value)}
                                />
                            </label>
                        </div>

                     
                    </section>
                </div>

                <div className="clientPremiumCard clientPremiumStripeCard clientPremiumContactsCard">
                    <div className="clientPremiumStripeHeader">
                        <div className="clientPremiumSectionIcon clientPremiumPurpleIcon">
                            <svg viewBox="0 0 24 24">
                                <path d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4ZM8 12a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm8 2c-3.31 0-6 1.79-6 4v1h12v-1c0-2.21-2.69-4-6-4ZM8 14c-2.76 0-5 1.49-5 3.33V19h5v-1c0-1.26.7-2.43 1.86-3.34A6.7 6.7 0 0 0 8 14Z" />
                            </svg>
                        </div>

                        <h3>Contacte</h3>

                        <button
                            type="button"
                            className="clientPremiumAddButton clientPremiumPurpleButton"
                            onClick={addContactForm}
                        >
                            + Adaugă contact
                        </button>
                    </div>

                    <div className="clientPremiumContactRows">
                        <div className="clientPremiumContactHeader">
                            <span>Nume</span>
                            <span>Funcție</span>
                            <span>Email</span>
                            <span>Telefon</span>
                            <span></span>
                        </div>

                        {contactsForm.map((contact, index) => (
                            <div className="clientPremiumContactRow" key={`contact-${index}`}>
                                <label className="clientPremiumField">
                                    <span>Nume</span>
                                    <div className="clientPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={contact.nume}
                                            onChange={(event) => updateContactForm(index, 'nume', event.target.value)}
                                            placeholder="Nume complet"
                                        />
                                    </div>
                                </label>

                                <label className="clientPremiumField">
                                    <span>Funcție</span>
                                    <div className="clientPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M20 6h-4V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v12h20V8a2 2 0 0 0-2-2Zm-6 0h-4V4h4v2Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={contact.functie}
                                            onChange={(event) => updateContactForm(index, 'functie', event.target.value)}
                                            placeholder="Funcție"
                                        />
                                    </div>
                                </label>

                                <label className="clientPremiumField">
                                    <span>Email</span>
                                    <div className="clientPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5v2Z" />
                                        </svg>

                                        <input
                                            type="email"
                                            value={contact.email}
                                            onChange={(event) => updateContactForm(index, 'email', event.target.value)}
                                            placeholder="email@exemplu.ro"
                                        />
                                    </div>
                                </label>

                                <label className="clientPremiumField">
                                    <span>Telefon</span>
                                    <div className="clientPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.58.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.58 1 1 0 0 1-.24 1.01l-2.21 2.2Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={contact.telefon}
                                            onChange={(event) => updateContactForm(index, 'telefon', event.target.value)}
                                            placeholder="07xx xxx xxx"
                                        />
                                    </div>
                                </label>

                                <button
                                    type="button"
                                    className="clientPremiumRemoveButton"
                                    disabled={contactsForm.length === 1}
                                    onClick={() => removeContactForm(index)}
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM8 4l1-1h6l1 1h4v2H4V4h4Z" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="clientPremiumCard clientPremiumStripeCard clientPremiumAddressesCard">
                    <div className="clientPremiumStripeHeader">
                        <div className="clientPremiumSectionIcon clientPremiumGreenIcon">
                            <svg viewBox="0 0 24 24">
                                <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
                            </svg>
                        </div>

                        <h3>Adrese</h3>

                        <button
                            type="button"
                            className="clientPremiumAddButton clientPremiumGreenButton"
                            onClick={addAddressForm}
                        >
                            + Adaugă adresă
                        </button>
                    </div>

                    <div className="clientPremiumAddressRows">
                        <div className="clientPremiumAddressHeader">
                            <span>Nume adresă</span>
                            <span>Adresă completă</span>
                            <span></span>
                        </div>

                        {addressesForm.map((address, index) => (
                            <div className="clientPremiumAddressRow" key={`address-${index}`}>
                                <label className="clientPremiumField">
                                    <span>Nume adresă</span>
                                    <div className="clientPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={address.nume}
                                            onChange={(event) => updateAddressForm(index, 'nume', event.target.value)}
                                            placeholder="Ex: Sediu principal"
                                        />
                                    </div>
                                </label>

                                <label className="clientPremiumField clientPremiumAddressField">
                                    <span>Adresă completă</span>
                                    <div className="clientPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M20.5 3 15 5.2 9 3 3.5 5.2V21L9 18.8l6 2.2 5.5-2.2V3ZM10 5.4l4 1.47v11.73l-4-1.47V5.4Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={address.adresa}
                                            onChange={(event) => updateAddressForm(index, 'adresa', event.target.value)}
                                            placeholder="Stradă, număr, oraș, județ, cod poștal"
                                        />
                                    </div>
                                </label>

                                <button
                                    type="button"
                                    className="clientPremiumRemoveButton"
                                    disabled={addressesForm.length === 1}
                                    onClick={() => removeAddressForm(index)}
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM8 4l1-1h6l1 1h4v2H4V4h4Z" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </MediumModal>
    )
}

export default AddClient